import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { X, Check, FileText, Download, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const CHECKBOXES = [
  "Je m'engage a suivre mon meal planning 7j/7.",
  "Je m'engage a realiser mon cardio maison (30-45 min) selon le protocole.",
  "Je m'engage a poster mes preuves photo/video.",
  "J'accepte les controles aleatoires du coach.",
  "Je m'engage a une discipline totale pendant les 12 semaines.",
  "Je me presente aux 4 pesees officielles (J0 / S4 / S8 / S12).",
  "J'ai compris le fonctionnement de la caution et des paliers de restitution.",
  "J'accepte la clause de disqualification : tout manquement peut entrainer la disqualification et la perte de la caution, sous pouvoir discretionnaire exclusif de Taiau, decision sans appel.",
];

function generatePDF(actData) {
  const doc = new jsPDF();
  const margin = 20;
  let y = 25;

  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ACTE D\'ENGAGEMENT', 105, y, { align: 'center' });
  y += 8;
  doc.setFontSize(12);
  doc.text('CHALLENGE TRANSFORMATION LA MEUTE', 105, y, { align: 'center' });
  y += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('1er juillet - 23 septembre 2026', 105, y, { align: 'center' });
  y += 12;

  // Identity
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Participant : ${actData.user_name || 'Membre'}`, margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.text(`Objectif : ${actData.objectif_declare === 'gras' ? 'Perte de gras' : 'Prise de muscle'}`, margin, y);
  y += 6;
  doc.text(`Caution : ${actData.caution_montant?.toLocaleString() || '20 000'} XPF`, margin, y);
  y += 10;

  // Rules
  doc.setDrawColor(200);
  doc.line(margin, y, 190, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('RAPPEL DES REGLES', margin, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  const rules = [
    'Pesees officielles J0 / S4 / S8 / S12',
    'Caution 20 000 XPF en 4 versements de 5 000 XPF',
    'Paliers de restitution : x0,33 (S4), x0,66 (S8), x1,00 (S12)',
    '3 prix : Recomposition, Perte de gras, Prise de muscle (1 prix max)',
    'Cautions non recuperees = cagnotte des lots',
  ];
  rules.forEach(r => {
    doc.text(`• ${r}`, margin + 2, y);
    y += 5;
  });
  y += 5;

  // Engagements
  doc.line(margin, y, 190, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('ENGAGEMENTS', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  CHECKBOXES.forEach((cb, i) => {
    const lines = doc.splitTextToSize(`[x] ${i + 1}. ${cb}`, 165);
    lines.forEach(line => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(line, margin + 2, y);
      y += 4.5;
    });
    y += 1;
  });

  y += 8;
  doc.line(margin, y, 190, y);
  y += 8;

  // Signature
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const signDate = actData.signed_at ? new Date(actData.signed_at).toLocaleString('fr-FR', { timeZone: 'Pacific/Tahiti', dateStyle: 'full', timeStyle: 'short' }) : 'N/A';
  doc.text(`Accepte et signe electroniquement le ${signDate}`, margin, y);
  y += 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`(heure de Tahiti) — Version : ${actData.rules_version || 'v1-2026-07'}`, margin, y);

  return doc;
}

export function EngagementModal({ isOpen, onClose, userName, onSigned }) {
  const [checks, setChecks] = useState(Array(8).fill(false));
  const [signing, setSigning] = useState(false);
  const allChecked = checks.every(Boolean);

  if (!isOpen) return null;

  const toggle = (i) => setChecks(prev => prev.map((v, j) => j === i ? !v : v));

  const handleSign = async () => {
    if (!allChecked) return;
    setSigning(true);
    try {
      const res = await apiClient.post('/engagement/sign', { checkboxes: checks });
      toast.success('Acte signe avec succes !');
      // Generate PDF
      try {
        const pdf = generatePDF(res.data);
        pdf.save(`acte-engagement-${userName || 'membre'}.pdf`);
      } catch { /* PDF generation optional */ }
      onSigned?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F0F0F] overflow-y-auto" data-testid="engagement-modal">
      <div className="max-w-lg mx-auto px-4 py-6 pb-24">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white z-10" data-testid="close-engagement">
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <Shield className="w-12 h-12 text-[#FF6B35] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white">ACTE D&#39;ENGAGEMENT</h1>
          <p className="text-sm text-[#FF6B35] mt-1">CHALLENGE TRANSFORMATION LA MEUTE</p>
          <p className="text-xs text-zinc-500 mt-1">1er juillet → 23 septembre 2026</p>
          <p className="text-sm text-white mt-2">{userName} · Caution : 20 000 XPF</p>
        </div>

        {/* Rules */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4 mb-4">
          <h3 className="text-xs font-bold text-[#FF6B35] uppercase tracking-wider mb-2">Rappel des regles</h3>
          <ul className="space-y-1.5 text-xs text-zinc-400">
            <li>&#8226; Pesees officielles J0 / S4 / S8 / S12</li>
            <li>&#8226; Caution 20 000 XPF en 4 versements de 5 000 XPF</li>
            <li>&#8226; Paliers : x0,33 (S4), x0,66 (S8), x1,00 (S12) — regle OU (gras OU muscle)</li>
            <li>&#8226; 3 prix : Recomposition, Perte de gras, Prise de muscle (1 max par participant)</li>
            <li>&#8226; Cautions non recuperees → cagnotte des lots</li>
          </ul>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3 mb-6">
          <h3 className="text-xs font-bold text-white uppercase tracking-wider">Mes engagements (8/8 requis)</h3>
          {CHECKBOXES.map((label, i) => (
            <button
              key={i}
              onClick={() => toggle(i)}
              className={`w-full flex items-start gap-3 p-3 rounded-sm text-left transition-all ${
                checks[i] ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/40' : 'bg-[#1B1B1B] border border-[#2A2A2E]'
              }`}
              data-testid={`engagement-check-${i}`}
            >
              <div className={`w-6 h-6 rounded-sm flex-shrink-0 flex items-center justify-center mt-0.5 ${
                checks[i] ? 'bg-[#FF6B35] text-black' : 'border-2 border-zinc-600'
              }`}>
                {checks[i] && <Check className="w-4 h-4" />}
              </div>
              <span className={`text-xs leading-relaxed ${checks[i] ? 'text-white' : 'text-zinc-400'}`}>
                {i + 1}. {label}
              </span>
            </button>
          ))}
        </div>

        {/* Sign button */}
        <div className="sticky bottom-4 bg-[#0F0F0F] pt-3">
          <Button
            onClick={handleSign}
            disabled={!allChecked || signing}
            className={`w-full py-4 text-base font-bold ${
              allChecked ? 'bg-[#FF6B35] text-black hover:bg-[#E85A25]' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
            }`}
            data-testid="sign-engagement-btn"
          >
            {signing ? 'Signature en cours...' : allChecked ? 'JE SIGNE ET JE M\'ENGAGE' : `${checks.filter(Boolean).length}/8 engagements coches`}
          </Button>
          <p className="text-[9px] text-zinc-600 text-center mt-2">
            Signature electronique horodatee, valant acceptation pleine et entiere du reglement.
          </p>
        </div>
      </div>
    </div>
  );
}

export function EngagementStatus({ userName }) {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/engagement/status');
      setStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!status) return null;

  const downloadPDF = () => {
    if (status.act) {
      const pdf = generatePDF(status.act);
      pdf.save(`acte-engagement-${userName || 'membre'}.pdf`);
    }
  };

  if (status.signed) {
    const signDate = new Date(status.act.signed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div className="bg-[#1B1B1B] border border-[#2e7d32]/30 rounded-lg p-4" data-testid="engagement-signed">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2e7d32]/20 flex items-center justify-center">
            <Check className="w-5 h-5 text-[#2e7d32]" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Acte signe le {signDate}</p>
            <p className="text-xs text-[#2e7d32]">Challenge actif</p>
          </div>
          <button onClick={downloadPDF} className="p-2 text-zinc-400 hover:text-[#FF6B35]" data-testid="download-pdf-btn">
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (!status.caution_ok) {
    return (
      <div className="bg-[#1B1B1B] border border-[#FFB800]/30 rounded-lg p-4" data-testid="engagement-no-caution">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-[#FFB800]" />
          <div>
            <p className="text-sm text-white">Caution en attente de validation</p>
            <p className="text-xs text-zinc-500">Le coach doit valider ta caution avant de signer</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-lg p-4 text-left hover:border-[#FF6B35] transition-colors"
        data-testid="sign-engagement-link"
      >
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-[#FF6B35]" />
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Signer l&#39;acte d&#39;engagement</p>
            <p className="text-xs text-[#FF6B35]">Caution validee — Signature requise</p>
          </div>
        </div>
      </button>
      <EngagementModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        userName={userName}
        onSigned={() => fetchStatus()}
      />
    </>
  );
}
